<script>
import _ from "lodash";
import moment from "moment";

import thumbnails from "./thumbnails.vue";
import approverThumbnails from "./approver-thumbnails.vue";
import timelineFeed from "./timeline.vue";
import proofToggles from "../global/proof-toggle";
import modalEditTags from "../modals/modal-edittags.vue";
import editSideBarPropertyModal from "../modals/modal-edit-sidebar-property";
import modalEditApprovers from "../modals/modal-editapprovers.vue";
import { PROOF_ACCESS_PERMISSIONS } from "../../utils/constants";
import { proofFilePreviewPending } from "../../utils/helpers";

const { EVERYTHING } = PROOF_ACCESS_PERMISSIONS;

const STATUS_COLORS = {
  approved: { bg: "#019256", soft: "rgba(1,146,86,0.10)", text: "#017a48", label: "Approved" },
  approvedWithChanges: { bg: "#019256", soft: "rgba(1,146,86,0.10)", text: "#017a48", label: "Approved With Changes" },
  notApproved: { bg: "#EB4654", soft: "rgba(235,70,84,0.10)", text: "#B43643", label: "Not Approved" },
  waiting: { bg: "#D9B116", soft: "rgba(217,177,22,0.12)", text: "#7A6207", label: "Waiting for Review" },
  created: { bg: "#6B7280", soft: "rgba(107,114,128,0.10)", text: "#4b5563", label: "Created" },
  none: { bg: "#6B7280", soft: "rgba(107,114,128,0.10)", text: "#4b5563", label: "—" },
};

function pickStatus(proof) {
  const s = proof && proof.status;
  if (s === 5) return STATUS_COLORS.approved;
  if (s === 6) return STATUS_COLORS.approvedWithChanges;
  if (s === 4) return STATUS_COLORS.notApproved;
  if (s === 2 || s === 3) return STATUS_COLORS.waiting;
  if (s === 1) return STATUS_COLORS.created;
  return STATUS_COLORS.none;
}

function stageStatusMeta(stage) {
  if (!stage) return STATUS_COLORS.none;
  switch (stage.statusType) {
    case 1: return STATUS_COLORS.none;
    case 3: return STATUS_COLORS.waiting;
    case 4: return STATUS_COLORS.notApproved;
    default: return STATUS_COLORS.approved;
  }
}

// Mirrors WorkflowStageApproverStatus enum in the API:
// 0 NotStarted, 1 Sent, 2 Waiting, 3 NotApproved, 4 Approved, 5 ApprovedWithChanges, 6 Skipped
function approverStatusMeta(approver) {
  switch (approver.workflowStageApproverStatus) {
    case 1:
    case 2: return STATUS_COLORS.waiting;
    case 3: return STATUS_COLORS.notApproved;
    case 4: return STATUS_COLORS.approved;
    case 5: return STATUS_COLORS.approvedWithChanges;
    default: return STATUS_COLORS.none;
  }
}

const SINGLE_STAGE_APPROVERS_SHOWN = 5;

const RAIL_WIDTH_KEY = "proof-details::rail-width";
const RAIL_MIN_WIDTH = 280;
const RAIL_MAX_WIDTH = 720;

function defaultRailWidth() {
  // Wider default on large monitors so the proof preview isn't tiny
  const vw = window.innerWidth || 1280;
  return Math.min(480, Math.max(320, Math.round(vw * 0.22)));
}

function clampRailWidth(w) {
  return Math.min(RAIL_MAX_WIDTH, Math.max(RAIL_MIN_WIDTH, w));
}

export default {
  name: "ProofDetails",
  components: {
    thumbnails,
    approverThumbnails,
    timelineFeed,
    proofToggles,
    modalEditTags,
    editSideBarPropertyModal,
    modalEditApprovers,
  },
  props: {
    proof: { required: true, type: Object },
    user: { required: true, type: Object },
    teams: { required: true, type: Array },
    allUsers: { required: true, type: Array, default: () => [] },
    activeUserIds: { type: Object, default: () => ({}) },
  },
  data() {
    let railWidth = 0;
    try {
      railWidth = parseInt(window.localStorage.getItem(RAIL_WIDTH_KEY), 10) || 0;
    } catch (_e) { /* ignore */ }
    return {
      detailsOpen: false,
      optionsOpen: false,
      colorPerUserId: {},
      railWidth: railWidth > 0 ? clampRailWidth(railWidth) : defaultRailWidth(),
      isResizingRail: false,
      railDragStartX: 0,
      railDragStartWidth: 0,
      showAllApprovers: false,
    };
  },
  computed: {
    railStyle() {
      // Thumbnail grows with the rail so widening actually enlarges the preview
      const thumbHeight = Math.min(480, Math.max(200, Math.round(this.railWidth * 0.66)));
      return { "--rail-w": `${this.railWidth}px`, "--thumb-h": `${thumbHeight}px` };
    },
    statusMeta() {
      return pickStatus(this.proof);
    },
    // Second badge beside "Waiting for Review" until an approver opens the current version
    showNotViewed() {
      const p = this.proof;
      return !!p && (p.status === 2 || p.status === 3) && p.viewedByApprover === false;
    },
    canDeleteProof() {
      return _.get(this, "user.permissions.proofAccess") === EVERYTHING;
    },
    versionLabel() {
      const v =
        _.get(this.proof, "currentVersion.versionNumber", null) ||
        _.get(this.proof, "currentVersion.version", null);
      return v ? `Version ${v}` : "";
    },
    fileCount() {
      return _.get(this.proof, "currentVersion.versionFiles", []).length;
    },
    fileCountLabel() {
      const n = this.fileCount;
      return `${n} ${n === 1 ? "file" : "files"}`;
    },
    senderName() {
      return _.get(this.proof, "currentVersion.sender.name", "—");
    },
    teamsWithViewAccess() {
      return _.get(this.proof, "teamsWithViewAccess", []);
    },
    proofTeams() {
      return (this.teams || []).filter((t) => this.teamsWithViewAccess.includes(t.id));
    },
    dedupedCustomFields() {
      if (!this.proof.customFields || this.proof.customFields.length === 0) return [];
      const grouped = _.groupBy(this.proof.customFields, "accountProofPropertyId");
      return Object.values(grouped).map((group) => _.sortBy(group, "proofPropertyId")[0]);
    },
    activeViewerCount() {
      return Object.keys(this.activeUserIds || {}).length;
    },
    hasProcessedThumb() {
      const files = _.get(this.proof, "currentVersion.versionFiles", []);
      if (!files.length) return false;
      return files.some((f) => f.thumbnailFileKey && f.thumbnailFileKey.length > 0 && f.thumbnailFileKey.indexOf("static") === -1);
    },
    previewPending() {
      const files = _.get(this.proof, "currentVersion.versionFiles", []);
      return files.some(proofFilePreviewPending);
    },
    isProcessing() {
      // Full skeleton only while there's nothing to show yet and a conversion is
      // in flight; otherwise the carousel renders per-file null/preview slides.
      return !this.hasProcessedThumb && this.fileCount > 0 && this.previewPending;
    },
    processingFileName() {
      const files = _.get(this.proof, "currentVersion.versionFiles", []);
      const f = files[0];
      return (f && f.name) || "your file";
    },
    currentStageId() {
      return _.get(this.proof, "currentVersion.currentStage.id", 0);
    },
    currentStageName() {
      return _.get(this.proof, "currentVersion.currentStage.name", "");
    },
    stages() {
      const _stages = _.get(this.proof, "currentVersion.stages", []) || [];
      _stages.forEach((s) => {
        (s.approvers || []).forEach((a) => { a.color = this.getColor(a.id); });
        (s.reviewers || []).forEach((r) => { r.color = this.getColor(r.id); });
        s.all_users = [...(s.approvers || []), ...(s.reviewers || [])];
      });
      return _stages;
    },
    singleStage() {
      return this.stages.length === 1 ? this.stages[0] : null;
    },
    singleStageParticipants() {
      if (!this.singleStage) return [];
      return [
        ...(this.singleStage.approvers || []).map((a) => ({ ...a, isApprover: true })),
        ...(this.singleStage.reviewers || []),
      ];
    },
    visibleParticipants() {
      if (this.showAllApprovers) return this.singleStageParticipants;
      return this.singleStageParticipants.slice(0, SINGLE_STAGE_APPROVERS_SHOWN);
    },
    hiddenParticipantCount() {
      return Math.max(0, this.singleStageParticipants.length - SINGLE_STAGE_APPROVERS_SHOWN);
    },
    dueDateText() {
      if (!this.proof.dueDate) return "No deadline";
      return this.$A.FormatDateToString(this.proof.dueDate, true);
    },
    dueDateOverdue() {
      if (!this.proof.dueDate) return false;
      if (this.proof.status === 5 || this.proof.status === 6) return false;
      return moment(this.proof.dueDate).endOf("day").isBefore(moment());
    },
  },
  beforeDestroy() {
    this.stopRailResize();
  },
  methods: {
    startRailResize(e) {
      this.isResizingRail = true;
      this.railDragStartX = e.clientX;
      this.railDragStartWidth = this.railWidth;
      window.addEventListener("mousemove", this.onRailResize);
      window.addEventListener("mouseup", this.stopRailResize);
      e.preventDefault();
    },
    onRailResize(e) {
      if (!this.isResizingRail) return;
      this.railWidth = clampRailWidth(this.railDragStartWidth + (e.clientX - this.railDragStartX));
    },
    stopRailResize() {
      if (!this.isResizingRail) return;
      this.isResizingRail = false;
      window.removeEventListener("mousemove", this.onRailResize);
      window.removeEventListener("mouseup", this.stopRailResize);
      try {
        window.localStorage.setItem(RAIL_WIDTH_KEY, String(this.railWidth));
      } catch (_e) { /* ignore */ }
    },
    resetRailWidth() {
      this.railWidth = defaultRailWidth();
      try {
        window.localStorage.removeItem(RAIL_WIDTH_KEY);
      } catch (_e) { /* ignore */ }
    },
    getColor(userId, luminosity = "dark") {
      if (this.colorPerUserId[userId]) return this.colorPerUserId[userId];
      let seed = Object.keys(this.colorPerUserId).length * 1000;
      let color = this.$randomColor({ luminosity, seed });
      this.colorPerUserId[userId] = color;
      return color;
    },
    formatStageStatus(status) {
      return status ? status.replace("/", " of ") : "";
    },
    editProperty(prop) {
      this.$refs.editSideBarPropertyModal.editProperty(this.proof.id, prop);
    },
    refreshCustomProperties: async function () {
      // Trigger reload via the root event other consumers listen for
      this.$root.$emit("proof::custom-props::updated");
    },
    toggleWorkflowSidebar() {
      if (this.proof.archived) {
        this.$A.AlertUser("Proof Is Archived. Can not Edit.");
        return;
      }
      this.$root.$emit("toggle::edit::workflow::sidebar");
    },
    openSendMessage() {
      this.$root.$emit("show::send::message::modal", 1);
    },
    viewProof() {
      this.$A.ProofService.NavigateToProofReviewScreen(this.proof, true);
    },
    exportTimeline($event) {
      this.$emit("exportTimeline", $event);
    },
    openStageDetails(stage) {
      if (this.proof.archived) {
        this.$A.AlertUser("Proof Is Archived. Can not Edit.");
        return;
      }
      this.$refs.editApproversModal.open(this.proof, stage);
    },
    stagePillStyle(stage) {
      const meta = stageStatusMeta(stage);
      return `background:${meta.soft};color:${meta.text};`;
    },
    approverPillStyle(approver) {
      const meta = approverStatusMeta(approver);
      return `background:${meta.soft};color:${meta.text};`;
    },
    approverStatusLabel(approver) {
      return approver.workflowStageApproverStatusLabel || approverStatusMeta(approver).label;
    },
    formatCustomValue(prop) {
      if (!prop.proofPropertyValue || prop.proofPropertyValue.length === 0) return "—";
      if (prop.accountProofPropertyDataType === "Datepicker") {
        return this.$A.FormatDateToString(prop.proofPropertyValue[0], true);
      }
      return prop.proofPropertyValue.join(", ");
    },
    routeBackHome() {
      this.$A.NavigateToView("/proof-list", {});
    },
  },
};
</script>

<template>
  <div class="proof-shell" :class="{ 'is-resizing': isResizingRail }">

    <!-- ============== RAIL ============== -->
    <aside class="info-rail" :style="railStyle">
      <div class="rail-scroll">

        <!-- Hero: name, ID/version, status -->
        <div class="rail-hero">
          <div class="hero-name-row">
            <button class="hero-name" v-b-modal.modalEditName :title="proof.name" data-cy="editProofNameBtn">
              {{ proof.name }}
            </button>
            <a v-b-modal.modalEditName class="hero-edit-btn" aria-label="Edit proof name">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </a>
          </div>

          <div class="hero-meta">
            <span class="hero-status-badge" :style="`background:${statusMeta.soft};color:${statusMeta.text};`">
              <span class="hero-status-dot" :style="`background:${statusMeta.bg};`"></span>
              {{ statusMeta.label }}
            </span>
            <span
              v-if="showNotViewed"
              class="hero-status-badge"
              :style="`background:${statusMeta.soft};color:${statusMeta.text};`"
              title="No approver has opened this version yet"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
              Not yet viewed
            </span>
            <span v-if="proof.archived" class="hero-archived-pill">Archived</span>
          </div>

          <div class="hero-subline">
            <span>#{{ proof.id }}</span>
            <span v-if="versionLabel" class="hero-dot">·</span>
            <span v-if="versionLabel">{{ versionLabel }}</span>
            <span class="hero-dot">·</span>
            <span>{{ fileCountLabel }}</span>
          </div>
        </div>

        <!-- Thumbnails -->
        <div class="rail-thumbs-wrap" id="proof_thumbnail">
          <div v-if="isProcessing" class="thumb-processing" role="status" aria-live="polite">
            <div class="thumb-processing-skeleton">
              <div class="thumb-processing-shimmer"></div>
            </div>
            <div class="thumb-processing-status">
              <span class="thumb-processing-dot">
                <span class="thumb-processing-dot-inner"></span>
              </span>
              <div class="thumb-processing-text">
                <div class="thumb-processing-title">Generating preview</div>
                <div class="thumb-processing-sub">Check back soon.</div>
              </div>
            </div>
          </div>
          <div v-else class="rail-thumbs">
            <thumbnails :proof="proof" />
          </div>
        </div>

        <!-- Details accordion (closed by default) -->
        <div class="rail-accordion" :class="{ 'is-open': detailsOpen }">
          <button type="button" class="rail-accordion-toggle" @click="detailsOpen = !detailsOpen" :aria-expanded="detailsOpen ? 'true' : 'false'">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="9" y1="13" x2="15" y2="13"/>
              <line x1="9" y1="17" x2="15" y2="17"/>
            </svg>
            <span class="rail-accordion-title">Details</span>
            <svg class="rail-accordion-caret" :class="{ 'is-open': detailsOpen }" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="1 1 5 5 9 1" />
            </svg>
          </button>
          <div v-show="detailsOpen" class="rail-accordion-body">

            <div class="rail-meta-block">
              <div class="rail-meta-item" :class="{ 'is-overdue': dueDateOverdue }">
                <span class="meta-label">Due date</span>
                <button type="button" class="meta-value-editable" v-b-modal.EditDeadline>
                  <svg v-if="dueDateOverdue" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="meta-overdue-icon">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span class="meta-value-text">{{ dueDateText }}</span>
                  <span v-if="dueDateOverdue" class="meta-overdue-text">Overdue</span>
                </button>
              </div>

              <div class="rail-meta-item">
                <span class="meta-label">Owner</span>
                <button type="button" class="meta-value-editable" v-b-modal.EditProofOwner>
                  <span class="meta-value-text">{{ senderName }}</span>
                </button>
              </div>

              <div class="rail-meta-item" v-if="teams && teams.length > 0">
                <span class="meta-label">Teams</span>
                <button type="button" class="meta-value-editable" v-b-modal.EditProofTeam>
                  <span v-if="proofTeams.length" class="meta-chips">
                    <span v-for="team in proofTeams" :key="team.id" class="meta-chip">{{ team.name }}</span>
                  </span>
                  <span v-else class="meta-value-text meta-empty">No team assigned</span>
                </button>
              </div>

              <div class="rail-meta-item">
                <span class="meta-label">Tags</span>
                <button type="button" class="meta-value-editable" @click="$refs.editProofTags.openModal()">
                  <span v-if="proof.tags && proof.tags.length" class="meta-chips">
                    <a
                      v-for="tag in proof.tags"
                      :key="tag.id"
                      class="meta-chip meta-chip-link"
                      @click.stop="$A.NavigateToView('/proof-list', { tag })"
                    >{{ tag.name }}</a>
                  </span>
                  <span v-else class="meta-value-text meta-empty">No tags</span>
                </button>
              </div>

              <div class="rail-meta-item" v-for="prop in dedupedCustomFields" :key="prop.accountProofPropertyId">
                <span class="meta-label">{{ prop.accountProofPropertyLabel }}</span>
                <button type="button" class="meta-value-editable" @click="editProperty(prop)">
                  <span v-if="prop.accountProofPropertyDataType === 'Checkboxes (Multiple Options)' && prop.proofPropertyValue && prop.proofPropertyValue.length" class="meta-chips">
                    <span v-for="(v, i) in prop.proofPropertyValue" :key="i" class="meta-chip">{{ v }}</span>
                  </span>
                  <span v-else class="meta-value-text">{{ formatCustomValue(prop) }}</span>
                </button>
              </div>
            </div>

          </div>
        </div>

        <!-- Options accordion (closed by default) -->
        <div class="rail-accordion" :class="{ 'is-open': optionsOpen }" id="proof_options">
          <button type="button" class="rail-accordion-toggle" @click="optionsOpen = !optionsOpen" :aria-expanded="optionsOpen ? 'true' : 'false'">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <span class="rail-accordion-title">Settings</span>
            <svg class="rail-accordion-caret" :class="{ 'is-open': optionsOpen }" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="1 1 5 5 9 1" />
            </svg>
          </button>
          <div v-show="optionsOpen" class="rail-accordion-body">
            <proofToggles :proof="proof" />
          </div>
        </div>

        <!-- Actions -->
        <div class="rail-actions">
          <a
            id="proof_link"
            v-show="!proof.archived"
            v-b-modal.reviewLink
            class="rail-action rail-action-primary"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            <span>Get review link</span>
          </a>

          <button
            id="proof_view"
            type="button"
            class="rail-action rail-action-secondary"
            @click="viewProof"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <span>View proof</span>
          </button>

          <button
            id="proof_version"
            v-show="!proof.archived"
            v-b-modal.SendNewVersion
            type="button"
            class="rail-action rail-action-secondary"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>Send new version</span>
          </button>

          <button
            v-show="!proof.archived"
            type="button"
            class="rail-action rail-action-secondary"
            @click="toggleWorkflowSidebar"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
            </svg>
            <span>Edit workflow</span>
          </button>

          <button
            id="proof_message"
            v-show="!proof.archived"
            v-b-modal.UpdatedProofOptions
            type="button"
            class="rail-action rail-action-ghost"
            @click="openSendMessage"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span>Send message</span>
          </button>

          <button
            v-b-modal.AddTimelineNote
            type="button"
            class="rail-action rail-action-ghost"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
            <span>Add internal note</span>
          </button>

          <button
            id="manage_proof_files"
            v-show="!proof.archived"
            v-b-modal.ManageFiles
            type="button"
            class="rail-action rail-action-ghost"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            <span>Manage versions</span>
          </button>

          <button
            id="export_timeline"
            type="button"
            class="rail-action rail-action-ghost"
            @click="exportTimeline"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>Export timeline</span>
          </button>

          <div v-show="!proof.archived" class="rail-action-separator" aria-hidden="true"></div>
          <button
            v-show="!proof.archived"
            v-b-modal.archiveProofModal
            type="button"
            class="rail-action rail-action-ghost"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="21 8 21 21 3 21 3 8" />
              <rect x="1" y="3" width="22" height="5" />
              <line x1="10" y1="12" x2="14" y2="12" />
            </svg>
            <span>Archive</span>
          </button>

          <button
            v-show="proof.archived"
            v-b-modal.restoreProofModal
            type="button"
            class="rail-action rail-action-primary"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
            <span>Restore proof</span>
          </button>

          <button
            v-show="proof.archived && canDeleteProof"
            v-b-modal.deleteProofModal
            type="button"
            class="rail-action rail-action-danger"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
              <line x1="10" y1="11" x2="10" y2="17" />
              <line x1="14" y1="11" x2="14" y2="17" />
            </svg>
            <span>Delete proof</span>
          </button>
        </div>
      </div>

      <div
        class="rail-resizer"
        title="Drag to resize · Double-click to reset"
        @mousedown="startRailResize"
        @dblclick="resetRailWidth"
      ></div>
    </aside>

    <!-- ============== MAIN COLUMN ============== -->
    <section class="proof-main-col">

      <div class="content-view">
        <div class="col-inner content-inner">

          <!-- ====== WORKFLOW CARD ====== -->
          <section class="detail-card" id="proof_stages">
            <div class="card-head">
              <div class="card-head-title">
                <span>Workflow</span>
              </div>
              <div class="card-head-actions">
                <div v-if="currentStageName" class="card-head-sub">
                  Currently on
                  <strong>{{ currentStageName }}</strong>
                </div>
                <button type="button" class="card-head-action" @click="toggleWorkflowSidebar">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                  </svg>
                  Edit workflow
                </button>
              </div>
            </div>

            <!-- Single-stage: expanded view with stage status + approver roster -->
            <div v-if="singleStage" class="single-stage">
              <button
                type="button"
                class="single-stage-head"
                :id="'approverStageRow_' + singleStage.id"
                @click="openStageDetails(singleStage)"
              >
                <span class="stage-dot is-current"></span>
                <span class="stage-row-name">{{ singleStage.name }}</span>
                <span class="stage-pill" :style="stagePillStyle(singleStage)">{{ formatStageStatus(singleStage.stageStatus) }}</span>
                <span class="single-stage-manage">
                  Manage
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </span>
              </button>

              <ul v-if="singleStageParticipants.length" class="approver-lines">
                <li
                  v-for="participant in visibleParticipants"
                  :key="participant.id"
                  class="approver-line"
                >
                  <span class="approver-avatar" :style="`background:${participant.color};`">
                    {{ participant.initials }}
                    <span v-if="activeUserIds[participant.id]" class="approver-online-dot"></span>
                  </span>
                  <span class="approver-line-name">{{ participant.name }}</span>
                  <span v-if="participant.isApprover" class="stage-pill" :style="approverPillStyle(participant)">{{ approverStatusLabel(participant) }}</span>
                  <span v-else class="stage-pill approver-pill-reviewer">Commenting Only</span>
                </li>
              </ul>
              <div v-else class="card-empty">
                <p>Add a contact to this proof by sharing your review link, or editing the workflow.</p>
              </div>

              <button
                v-if="hiddenParticipantCount > 0"
                type="button"
                class="approver-toggle"
                @click="showAllApprovers = !showAllApprovers"
              >
                <span v-if="!showAllApprovers">View {{ hiddenParticipantCount }} more</span>
                <span v-else>View less</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="{ 'is-open': showAllApprovers }" class="approver-toggle-chevron">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </div>

            <div v-else-if="stages && stages.length" class="stage-list">
              <button
                v-for="stage in stages"
                :key="stage.id"
                :id="'approverStageRow_' + stage.id"
                type="button"
                class="stage-row"
                :class="{ 'is-current': stage.id === currentStageId }"
                @click="openStageDetails(stage)"
              >
                <div class="stage-row-marker">
                  <span class="stage-dot" :class="{ 'is-current': stage.id === currentStageId }"></span>
                </div>
                <div class="stage-row-main">
                  <span class="stage-row-name">{{ stage.name }}</span>
                  <span class="stage-pill" :style="stagePillStyle(stage)">{{ formatStageStatus(stage.stageStatus) }}</span>
                </div>
                <div class="stage-row-reviewers" @click.stop>
                  <approver-thumbnails :approvers="stage.all_users" :active-ids="activeUserIds" />
                </div>
                <div class="stage-row-action" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </button>
            </div>

            <div v-else class="card-empty">
              <p>Add a contact to this proof by sharing your review link, or editing the workflow.</p>
            </div>
          </section>

          <!-- ====== TIMELINE / NEWSFEED CARD ====== -->
          <section class="detail-card timeline-card">
            <timelineFeed :all-users="allUsers" :proof="proof" />
          </section>

        </div>
      </div>
    </section>

    <modalEditTags :proof="proof" ref="editProofTags" />
    <editSideBarPropertyModal
      ref="editSideBarPropertyModal"
      v-on:propertyUpdated="refreshCustomProperties"
    />
    <modalEditApprovers ref="editApproversModal" />
  </div>
</template>

<style scoped>
/* ===== SHELL ===== */
.proof-shell {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  background: #fff;
  position: relative;
}

/* ===== RAIL ===== */
.info-rail {
  width: var(--rail-w, 320px);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fbfbfc;
  border-right: 1px solid rgba(15, 23, 42, 0.08);
  overflow: hidden;
  position: relative;
}

.rail-resizer {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 9px;
  cursor: col-resize;
  z-index: 5;
}
.rail-resizer::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 3px;
  background: transparent;
  transition: background-color 120ms ease;
}
.rail-resizer:hover::after,
.proof-shell.is-resizing .rail-resizer::after {
  background: rgba(1, 146, 86, 0.45);
}

.proof-shell.is-resizing {
  cursor: col-resize;
  user-select: none;
}

.rail-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 18px 24px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  scrollbar-width: thin;
}
.rail-scroll::-webkit-scrollbar { width: 8px; }
.rail-scroll::-webkit-scrollbar-thumb {
  background: rgba(15, 23, 42, 0.12);
  border-radius: 999px;
  border: 2px solid #fbfbfc;
}
.rail-scroll::-webkit-scrollbar-thumb:hover { background: rgba(15, 23, 42, 0.22); }

/* ===== HERO ===== */
.rail-hero {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.hero-name-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  position: relative;
}
.hero-name {
  flex: 1;
  background: transparent;
  border: 0;
  padding: 0;
  text-align: left;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
  letter-spacing: -0.01em;
  cursor: pointer;
  word-break: break-word;
  transition: color 120ms ease;
}
.hero-name:hover { color: #019256; }
.hero-edit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  color: #9ca3af;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
  flex-shrink: 0;
  margin-top: 2px;
}
.hero-edit-btn:hover { background: rgba(1, 146, 86, 0.10); color: #019256; }

.hero-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.hero-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.01em;
}
.hero-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  flex-shrink: 0;
}
.hero-id {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}
.hero-archived-pill {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  font-size: 10px;
  font-weight: 700;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 999px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.hero-subline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #9ca3af;
}
.hero-dot { opacity: 0.5; }

/* ===== THUMBNAILS WRAP ===== */
.rail-thumbs-wrap {
  position: relative;
  min-height: var(--thumb-h, 200px);
  flex: 0 0 auto;
}
.rail-thumbs {
  position: relative;
  height: var(--thumb-h, 200px);
  min-height: var(--thumb-h, 200px);
  border-radius: 10px;
  overflow: hidden;
  isolation: isolate;
  border: 1px solid rgba(15, 23, 42, 0.06);
}
::v-deep .rail-thumbs > div {
  height: 100%;
  width: 100%;
}
::v-deep .rail-thumbs .carousel {
  border-radius: 0 !important;
  padding: 0 !important;
  background: #f4f5f7 !important;
  height: 100% !important;
  width: 100%;
}
::v-deep .rail-thumbs .carousel-inner,
::v-deep .rail-thumbs .carousel-item {
  height: 100%;
}
::v-deep .rail-thumbs .carousel-ashore-image,
::v-deep .rail-thumbs .carousel-null-thumb {
  height: var(--thumb-h, 200px) !important;
}
::v-deep .rail-thumbs .carousel-indicators li {
  background: rgba(15, 23, 42, 0.25);
}
::v-deep .rail-thumbs .carousel-indicators .active {
  background: #019256;
}
::v-deep .rail-thumbs .loading-text { display: none !important; }

/* ===== PROCESSING STATE ===== */
.thumb-processing {
  position: relative;
  height: var(--thumb-h, 200px);
  border-radius: 10px;
  overflow: hidden;
  isolation: isolate;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
.thumb-processing-skeleton {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      ellipse at 30% 35%,
      rgba(1, 146, 86, 0.10) 0%,
      rgba(1, 146, 86, 0) 55%
    ),
    radial-gradient(
      ellipse at 75% 70%,
      rgba(15, 23, 42, 0.05) 0%,
      rgba(15, 23, 42, 0) 60%
    ),
    linear-gradient(135deg, #fbfbfc 0%, #f3f4f6 100%);
}
.thumb-processing-shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    100deg,
    transparent 0%,
    transparent 40%,
    rgba(255, 255, 255, 0.65) 50%,
    transparent 60%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: thumb-shimmer 1900ms ease-in-out infinite;
}
@keyframes thumb-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -100% 0; }
}
.thumb-processing-status {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}
.thumb-processing-dot {
  position: relative;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: rgba(1, 146, 86, 0.16);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.thumb-processing-dot::before {
  content: "";
  position: absolute;
  inset: -2px;
  border-radius: 999px;
  background: transparent;
  border: 2px solid rgba(1, 146, 86, 0.35);
  animation: thumb-ping 1500ms ease-out infinite;
}
@keyframes thumb-ping {
  0%   { transform: scale(0.85); opacity: 0.9; }
  100% { transform: scale(1.7);  opacity: 0; }
}
.thumb-processing-dot-inner {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #019256;
}
.thumb-processing-text { min-width: 0; }
.thumb-processing-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  line-height: 1.2;
}
.thumb-processing-sub {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===== ACTIONS ===== */
.rail-actions {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.rail-action {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  transition: background-color 120ms ease, color 120ms ease, border-color 120ms ease, transform 80ms ease;
  white-space: nowrap;
}
.rail-action svg { flex-shrink: 0; }
.rail-action:active { transform: scale(0.98); }
.rail-action:hover { text-decoration: none; }

.rail-action-primary {
  background: #019256;
  color: #fff;
  border-color: #019256;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}
.rail-action-primary:hover {
  background: #017a48;
  border-color: #017a48;
  color: #fff;
}

.rail-action-secondary {
  background: #fff;
  color: #1f2937;
  border-color: #e1e4ea;
}
.rail-action-secondary:hover {
  background: rgba(1, 146, 86, 0.06);
  border-color: rgba(1, 146, 86, 0.35);
  color: #017a48;
}

.rail-action-ghost {
  background: transparent;
  color: #4b5563;
}
.rail-action-ghost:hover {
  background: rgba(15, 23, 42, 0.04);
  color: #111827;
}

.rail-action-separator {
  height: 1px;
  background: rgba(15, 23, 42, 0.08);
  margin: 8px 4px 4px;
  flex-shrink: 0;
}

.rail-action-danger {
  background: transparent;
  color: #B43643;
  border-color: rgba(235, 70, 84, 0.30);
}
.rail-action-danger:hover {
  background: rgba(235, 70, 84, 0.08);
  border-color: rgba(235, 70, 84, 0.50);
  color: #9c2b37;
}

/* ===== META (lives inside Details accordion) ===== */
.rail-meta-block {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.rail-meta-item { display: flex; flex-direction: column; gap: 4px; }
.meta-label {
  font-size: 10px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.meta-value-editable {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 0;
  background: transparent;
  border: 0;
  text-align: left;
  cursor: pointer;
  border-radius: 0;
  font: inherit;
  color: inherit;
}
.meta-value-editable:focus { outline: none; }
.meta-value-editable:focus-visible {
  outline: 2px solid rgba(1, 146, 86, 0.40);
  outline-offset: 3px;
  border-radius: 3px;
}
.meta-value-text {
  font-size: 13px;
  color: #1f2937;
  font-weight: 500;
  line-height: 1.35;
  border-bottom: 1px dashed transparent;
  transition: border-color 140ms ease, color 140ms ease;
}
.meta-value-editable:hover .meta-value-text {
  color: #019256;
  border-bottom-color: rgba(1, 146, 86, 0.50);
}
.meta-empty {
  color: #9ca3af !important;
  font-style: italic;
}
.meta-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.meta-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: #4b5563;
  background: #f3f4f6;
  border: 1px solid transparent;
  transition: background-color 120ms ease, color 120ms ease, border-color 120ms ease;
}
.meta-value-editable:hover .meta-chip {
  background: rgba(1, 146, 86, 0.08);
  color: #017a48;
}
.meta-chip-link { cursor: pointer; }
.meta-chip-link:hover {
  background: rgba(1, 146, 86, 0.18) !important;
  color: #019256 !important;
  border-color: rgba(1, 146, 86, 0.20) !important;
}
.rail-meta-item.is-overdue .meta-value-text { color: #B43643; }
.meta-overdue-icon { color: #EB4654; }
.meta-overdue-text {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(235, 70, 84, 0.10);
  color: #B43643;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* ===== ACCORDION (Details + Options) ===== */
.rail-accordion {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 10px;
  background: #fff;
  transition: border-color 160ms ease, box-shadow 160ms ease;
  overflow: hidden;
}
.rail-accordion.is-open {
  border-color: rgba(1, 146, 86, 0.20);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}
.rail-accordion-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  background: transparent;
  border: 0;
  color: #4b5563;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.rail-accordion-toggle:hover {
  background: rgba(15, 23, 42, 0.025);
  color: #111827;
}
.rail-accordion-toggle svg:first-of-type {
  color: #019256;
  flex-shrink: 0;
}
.rail-accordion-title {
  flex: 1;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0;
  color: #1f2937;
}
.rail-accordion-caret {
  flex-shrink: 0;
  color: #6b7280;
  transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1);
}
.rail-accordion-caret.is-open { transform: rotate(180deg); color: #019256; }
.rail-accordion-body {
  padding: 12px 14px 16px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  animation: accordion-reveal 200ms ease;
}
/* Compact toggles inside Options accordion */
::v-deep .rail-accordion-body > .d-flex.flex-column.gap-10 {
  gap: 0 !important;
}
::v-deep .rail-accordion-body .ashore-toggle {
  align-items: center;
  padding: 9px 0;
  gap: 12px !important;
  border-bottom: 1px solid rgba(15, 23, 42, 0.04);
}
::v-deep .rail-accordion-body .ashore-toggle:last-child {
  border-bottom: 0;
}
::v-deep .rail-accordion-body .ashore-toggle .switch {
  width: 30px !important;
  height: 18px !important;
  min-width: 30px !important;
  margin-right: 0 !important;
  flex-shrink: 0;
}
::v-deep .rail-accordion-body .ashore-toggle .slider {
  width: 30px !important;
  height: 18px !important;
  padding: 0 !important;
  border-radius: 999px !important;
}
::v-deep .rail-accordion-body .ashore-toggle .slider:before {
  width: 12px !important;
  height: 12px !important;
  top: 3px !important;
  left: 3px !important;
}
::v-deep .rail-accordion-body .ashore-toggle input:checked + .slider:before {
  transform: translateX(12px) !important;
}
::v-deep .rail-accordion-body .ashore-toggle > .d-flex.gap-10 {
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 6px !important;
}
::v-deep .rail-accordion-body .ashore-toggle p {
  font-size: 12px;
  color: #1f2937;
  font-weight: 500;
  line-height: 1.35;
  flex: 1;
  min-width: 0;
}
::v-deep .rail-accordion-body .ashore-toggle .info-icon {
  width: 13px !important;
  height: 13px !important;
  opacity: 0.5;
  flex-shrink: 0;
}
.rail-accordion + .rail-accordion {
  margin-top: -14px;
}
@keyframes accordion-reveal {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.rail-accordion-body > * + * { margin-top: 14px; }

/* ===== MAIN COLUMN ===== */
.proof-main-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #f4f5f7;
}
@keyframes presence-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(1, 146, 86, 0.45); }
  50%      { box-shadow: 0 0 0 5px rgba(1, 146, 86, 0); }
}

/* ===== CONTENT ===== */
.content-view {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: transparent;
}
.content-inner {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  min-height: 100%;
  max-width: 991px;
  margin: 0 auto;
}
.detail-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: #fbfbfc;
}
.card-head-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
  letter-spacing: 0;
}
.card-head-sub {
  font-size: 12px;
  color: #6b7280;
}
.card-head-actions {
  display: inline-flex;
  align-items: center;
  gap: 14px;
}
.card-head-action {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: border-color 120ms ease, color 120ms ease, background-color 120ms ease;
}
.card-head-action:hover {
  color: #019256;
  border-color: rgba(1, 146, 86, 0.4);
  background: rgba(1, 146, 86, 0.04);
}
.card-head-action:focus { outline: none; }
.card-head-action:focus-visible {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.15);
}
.card-head-sub strong {
  color: #0f172a;
  font-weight: 700;
}
.card-empty {
  padding: 20px 20px 24px;
  font-size: 13px;
  color: #6b7280;
}

/* ===== STAGE LIST ===== */
.stage-list {
  display: flex;
  flex-direction: column;
}
.stage-row {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 16px 20px;
  position: relative;
  background: transparent;
  border: 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: background-color 120ms ease;
}
.stage-row:last-child { border-bottom: 0; }
.stage-row::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #019256;
  opacity: 0;
  transition: opacity 120ms ease;
}
.stage-row.is-current::before { opacity: 1; }
.stage-row:hover { background: #f9f9fa; }
.stage-row:hover .stage-row-action { color: #019256; transform: translateX(2px); }
.stage-row:focus { outline: none; }
.stage-row:focus-visible { background: rgba(1, 146, 86, 0.05); }
.stage-row-marker {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.stage-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #fff;
  border: 2px solid #d1d5db;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.stage-dot.is-current {
  background: #019256;
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.18);
}
.stage-row-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.stage-row-name {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stage-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.01em;
  width: max-content;
}
.stage-row-reviewers {
  flex-shrink: 0;
}
.stage-row-action {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #c2c8d0;
  transition: color 120ms ease, transform 160ms ease;
}

/* ===== SINGLE STAGE (expanded) ===== */
.single-stage {
  display: flex;
  flex-direction: column;
}
.single-stage-head {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 20px;
  background: transparent;
  border: 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: background-color 120ms ease;
}
.single-stage-head:hover { background: #f9f9fa; }
.single-stage-head:focus { outline: none; }
.single-stage-head:focus-visible { background: rgba(1, 146, 86, 0.05); }
.single-stage-manage {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  transition: color 120ms ease;
}
.single-stage-head:hover .single-stage-manage { color: #019256; }
.approver-lines {
  list-style: none;
  margin: 0;
  padding: 0;
}
.approver-line {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px 10px 30px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.04);
}
.approver-line:last-child { border-bottom: 0; }
.approver-avatar {
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}
.approver-online-dot {
  position: absolute;
  top: -1px;
  left: -1px;
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: #019256;
  border: 2px solid #fff;
}
.approver-line-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.approver-pill-reviewer {
  background: rgba(107, 114, 128, 0.10);
  color: #4b5563;
}
.approver-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 10px 20px;
  background: transparent;
  border: 0;
  border-top: 1px solid rgba(15, 23, 42, 0.04);
  font-size: 12px;
  font-weight: 600;
  color: #019256;
  cursor: pointer;
  transition: background-color 120ms ease;
}
.approver-toggle:hover { background: rgba(1, 146, 86, 0.05); }
.approver-toggle:focus { outline: none; }
.approver-toggle:focus-visible { background: rgba(1, 146, 86, 0.08); }
.approver-toggle-chevron { transition: transform 160ms ease; }
.approver-toggle-chevron.is-open { transform: rotate(180deg); }
@media (prefers-reduced-motion: reduce) {
  .approver-toggle-chevron { transition: none; }
}

/* ===== TIMELINE CARD: restyle reused <timeline> internals ===== */
.timeline-card {
  padding: 0;
}
::v-deep .timeline-card .timeline {
  padding: 0 !important;
  margin: 0 !important;
}
::v-deep .timeline-card .timeline > .nav {
  padding: 14px 20px !important;
  margin: 0 !important;
  background: #fbfbfc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06) !important;
  gap: 4px;
}
::v-deep .timeline-card .timeline .text-gray-700 {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #1f2937 !important;
  letter-spacing: 0;
}
::v-deep .timeline-card .timeline .real-time-feed {
  background: #019256 !important;
  width: 7px !important;
  height: 7px !important;
  border-radius: 999px !important;
  display: inline-block !important;
  animation: presence-pulse 1500ms ease-in-out infinite;
}
::v-deep .timeline-card .timeline .nav-item {
  margin: 0 2px;
  padding: 0 !important;
  background: transparent !important;
  border-radius: 999px !important;
}
::v-deep .timeline-card .timeline .status-rounded-pill-active,
::v-deep .timeline-card .timeline .status-rounded-pill-inactive {
  background: transparent !important;
}
::v-deep .timeline-card .timeline .nav-item .nav-link {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px !important;
  height: 28px;
  border-radius: 999px !important;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  background: transparent;
  border: 1px solid transparent;
  transition: background-color 120ms ease, color 120ms ease, border-color 120ms ease;
}
::v-deep .timeline-card .timeline .nav-item .nav-link:hover {
  color: #019256;
  background: rgba(1, 146, 86, 0.08);
}
::v-deep .timeline-card .timeline .status-rounded-pill-active .nav-link {
  background: rgba(1, 146, 86, 0.10) !important;
  color: #017a48 !important;
  border-color: rgba(1, 146, 86, 0.20);
}
::v-deep .timeline-card .timeline .status-rounded-pill-inactive .nav-link {
  color: #6b7280 !important;
}
::v-deep .timeline-card .timeline > [data-cy="timelineRow"] {
  position: relative;
  padding-left: 22px;
}
/* Continuous vertical rail running through events */
::v-deep .timeline-card .timeline > [data-cy="timelineRow"]::before {
  content: "";
  position: absolute;
  left: 39px;
  top: 16px;
  bottom: 16px;
  width: 2px;
  background: linear-gradient(
    to bottom,
    rgba(15, 23, 42, 0.04) 0%,
    rgba(15, 23, 42, 0.08) 14%,
    rgba(15, 23, 42, 0.08) 86%,
    rgba(15, 23, 42, 0.04) 100%
  );
  border-radius: 2px;
}
::v-deep .timeline-card .timeline .timeline-event {
  padding: 18px 20px 18px 0 !important;
  border-bottom: 0 !important;
  margin: 0 !important;
  position: relative;
  z-index: 1;
  min-width: 0;
}
::v-deep .timeline-card .timeline .timeline-event > .flex-fill {
  min-width: 0;
  word-break: break-word;
  overflow-wrap: anywhere;
}
::v-deep .timeline-card .timeline .timeline-icon-parent,
::v-deep .timeline-card .timeline .timeline-icon {
  display: inline-flex !important;
}
::v-deep .timeline-card .timeline .timeline-icon-parent {
  position: relative;
  width: 36px;
  min-width: 36px;
  flex-shrink: 0;
}
/* Long filenames inside the embedded FILES table can blow out the row */
::v-deep .timeline-card .timeline .files-approval-table,
::v-deep .timeline-card .timeline .table-responsive {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}
::v-deep .timeline-card .timeline .file-info-text {
  display: inline-block;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}
::v-deep .timeline-card .timeline .timeline-icon {
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  box-shadow: 0 0 0 4px #fff;
  overflow: hidden;
  transition: box-shadow 160ms ease, transform 160ms ease;
}
::v-deep .timeline-card .timeline .timeline-event:hover .timeline-icon {
  box-shadow: 0 0 0 4px #fff, 0 4px 12px rgba(15, 23, 42, 0.10);
  transform: translateY(-1px);
}
::v-deep .timeline-card .timeline .timeline-icon img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
}
::v-deep .timeline-card .timeline .event-time {
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
}
::v-deep .timeline-card .timeline .event-span {
  color: #9ca3af;
}
/* Normalize timeline event author + description to 13px (was 14px from Bootstrap / scoped styles) */
::v-deep .timeline-card .timeline .text-gray-900.fw-semibold {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
}
::v-deep .timeline-card .timeline .description-text {
  font-size: 13px !important;
  line-height: 1.45;
}
::v-deep .timeline-card .timeline .text-gray-500,
::v-deep .timeline-card .timeline .text-gray-600 {
  font-size: 13px;
  line-height: 1.45;
}

/* ===== MOBILE ===== */
@media (max-width: 720px) {
  .proof-shell {
    flex-direction: column;
  }
  .info-rail {
    width: 100%;
    border-right: 0;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    overflow: visible;
  }
  .rail-resizer { display: none; }
  .rail-thumbs-wrap { min-height: 200px; }
  .rail-thumbs,
  .thumb-processing {
    height: 200px;
    min-height: 200px;
  }
  ::v-deep .rail-thumbs .carousel-ashore-image,
  ::v-deep .rail-thumbs .carousel-null-thumb {
    height: 200px !important;
  }
  .rail-scroll {
    overflow: visible;
    padding: 14px 14px 18px;
    gap: 18px;
  }
  .content-view { overflow: visible; }
  .content-inner { min-height: 0; }
  .content-inner { padding: 10px 8px; gap: 12px; }
  .stage-row { padding: 12px 14px; gap: 10px; }
  .stage-row-reviewers { display: none; }
  .single-stage-head { padding: 12px 14px; gap: 10px; }
  .approver-line { padding: 10px 14px 10px 24px; }
  .card-head { padding: 12px 14px; }
  ::v-deep .timeline-card .timeline > [data-cy="timelineRow"] {
    padding-left: 0 !important;
  }
  ::v-deep .timeline-card .timeline > [data-cy="timelineRow"]::before {
    display: none !important;
  }
  ::v-deep .timeline-card .timeline .timeline-event {
    padding: 12px 14px !important;
    gap: 10px !important;
  }
  ::v-deep .timeline-card .timeline .timeline-icon {
    box-shadow: none !important;
  }
  ::v-deep .timeline-card .timeline > .nav { padding: 12px 14px !important; }
}
</style>
